import { CHANGE_V2_CURRENT_DATE } from '../constants';

const DAY_MS = 24 * 60 * 60 * 1000;

// Sentence-case a stored label: keep the first word, lowercase the rest, but
// preserve all-caps tokens (acronyms like QMS, CAPA, PMS, IFU).
export const toSentenceCase = (value: string): string => {
  const words = value.split(' ');
  return words
    .map((word, index) => {
      if (index === 0) return word;
      if (word === word.toUpperCase()) return word;
      return word.toLowerCase();
    })
    .join(' ');
};

export const formatDate = (value?: string): string => {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not set';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
};

export const formatDateTime = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  }).format(date);
};

export const getDayOffset = (value?: string): number | undefined => {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return Math.round((date.getTime() - CHANGE_V2_CURRENT_DATE.getTime()) / DAY_MS);
};

// A short relative-time phrase used in "recent activity" and audit lists.
export const formatRelativeTime = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const diffMs = CHANGE_V2_CURRENT_DATE.getTime() - date.getTime();
  const diffMinutes = Math.round(diffMs / (60 * 1000));
  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes} min ago`;
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} h ago`;
  const diffDays = Math.round(diffHours / 24);
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 30) return `${diffDays} days ago`;
  return formatDate(value);
};

export const getInitials = (name: string): string =>
  name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .slice(0, 2)
    .join('');
